import { useCallback, useMemo, useRef, useLayoutEffect, useEffect, useState } from 'react';

function _extends() {
  _extends = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends.apply(this, arguments);
}

function isPlainObject(value) {
  return Object.prototype.toString.call(value) === "[object Object]";
}

function isTransform(value) {
  return typeof value === "function";
}

function applyTransform(next, prev) {
  return isTransform(next) ? next(prev) : next;
}

function getProperty(data, key) {
  return isPlainObject(data) ? data[key] : undefined;
}

function useSetProperty(setState, name) {
  return useCallback(function (update) {
    setState(function (state) {
      var _extends2;

      return _extends({}, state, (_extends2 = {}, _extends2[name] = applyTransform(update, getProperty(state, name)), _extends2));
    });
  }, [setState, name]);
}
/**
 * Create a field for a given property.
 *
 * @example
 * const form = useForm({
 *   initialValue: {
 *     email: "",
 *     profile: { name: "" } }
 *   }
 * });
 *
 * const email = useField(form, "email");
 * const profile = useField(form, "profile");
 * const name = useField(profile, "name");
 */


function useField(field, name) {
  var value = getProperty(field.value, name);
  var error = getProperty(field.error, name);
  var touched = getProperty(field.touched, name);
  var setValue = useSetProperty(field.setValue, name);
  var setError = useSetProperty(field.setError, name);
  var setTouched = useSetProperty(field.setTouched, name);
  return useMemo(function () {
    return {
      id: field.id + "_" + name,
      name: name,
      value: value,
      error: error,
      touched: touched,
      setValue: setValue,
      setError: setError,
      setTouched: setTouched,
      isSubmitting: field.isSubmitting,
      isValidating: field.isValidating,
      submission: field.submission
    };
  }, [field.id, field.isSubmitting, field.isValidating, field.submission, name, value, error, touched, setValue, setError, setTouched]);
}

function getItem(data, index) {
  return Array.isArray(data) ? data[index] : undefined;
}

function useSetItem(setState, index) {
  return useCallback(function (update) {
    setState(function (state) {
      var nextState = Array.isArray(state) ? [].concat(state) : [];
      nextState[index] = applyTransform(update, getItem(state, index));
      return nextState;
    });
  }, [setState, index]);
}
/**
 * Create a field for a specific index in an array.
 *
 * This hook is intended for use in building forms with "Add another" functionality.
 *
 * @example
 * const form = useForm({ initialValue: { pets: [{ name: "" }] } });
 * const pets = useField(form, "pets");
 * const pet = useFieldItem(pets, 0);
 * const name = useField(pet, "name");
 */


function useFieldItem(field, index) {
  var value = getItem(field.value, index);
  var error = getItem(field.error, index);
  var touched = getItem(field.touched, index);
  var setValue = useSetItem(field.setValue, index);
  var setError = useSetItem(field.setError, index);
  var setTouched = useSetItem(field.setTouched, index);
  return useMemo(function () {
    return {
      id: field.id + "_" + index,
      name: index,
      value: value,
      error: error,
      touched: touched,
      setValue: setValue,
      setError: setError,
      setTouched: setTouched,
      isValidating: field.isValidating,
      isSubmitting: field.isSubmitting,
      submission: field.submission
    };
  }, [field.id, field.isValidating, field.isSubmitting, field.submission, index, value, error, touched, setValue, setError, setTouched]);
}

var generate = /*#__PURE__*/function () {
  var previousId = 0;
  return function () {
    return (previousId++).toString(36);
  };
}();
/**
 * Creates a unique identifier that will remain consistent across re-renders.
 *
 * This hook does not currently support SSR.
 */


var useIdentifier = function useIdentifier(id) {
  var ref = useRef(id);

  if (ref.current === undefined) {
    ref.current = generate();
  }

  return ref.current;
};

var useIsomorphicLayoutEffect = typeof window !== "undefined" && typeof window.document !== "undefined" && typeof window.document.createElement !== "undefined" ? useLayoutEffect : useEffect;
/**
 * @private
 *
 * Creates a new callback, scoped to the current props and state
 * on each render. The returned function will always maintain
 * the same identity, which avoids re-renders.
 */

function useEventCallback(fn) {
  var ref = useRef(fn);
  useIsomorphicLayoutEffect(function () {
    ref.current = fn;
  });
  return useCallback(function () {
    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }

    return ref.current.apply(void 0, args);
  }, []);
}

var initialSubmission = {
  count: 0
};
/**
 * Create a new form. A form requires an initial value, a function to validate,
 * and a submit handler.
 *
 * A form behaves just like any other field, but with some extra properties for
 * managing submission.
 *
 * The initial value for the form can be literally anything! Usually, it's an
 * object, but it could be any type of value.
 *
 * @example <caption>Form values can be primitive</caption>
 * const form = useForm({ initialValue: "" });
 *
 * @example <caption>But usually, they'll contain an object</caption>
 * const form = useForm({
 *   initialValue: {
 *     email: "",
 *     name: ""
 *   }
 * });
 */

function useForm(options) {
  var id = useIdentifier(options.id);
  var initialValue = useRef(options.initialValue);
  var initialError = useRef(options.initialError);
  var initialTouched = useRef(options.initialTouched);

  var _useState = useState(initialValue.current),
      value = _useState[0],
      setValue = _useState[1];

  var _useState2 = useState(initialError.current),
      error = _useState2[0],
      setError = _useState2[1];

  var _useState3 = useState(initialTouched.current),
      touched = _useState3[0],
      setTouched = _useState3[1];

  var _useState4 = useState(false),
      isValidating = _useState4[0],
      setValidating = _useState4[1];

  var _useState5 = useState(false),
      isSubmitting = _useState5[0],
      setSubmitting = _useState5[1];

  var _useState6 = useState(initialSubmission),
      submission = _useState6[0],
      setSubmission = _useState6[1];

  var validate = useEventCallback(function () {
    return Promise.resolve({
      value: value,
      valid: true
    });
  });
  var reset = useEventCallback(function (opts) {
    if (opts && "value" in opts) {
      initialValue.current = opts.value;
    }

    if (opts && "error" in opts) {
      initialError.current = opts.error;
    }

    if (opts && "touched" in opts) {
      initialTouched.current = opts.touched;
    }

    setValue(initialValue.current);
    setError(initialError.current);
    setTouched(initialTouched.current);
  });
  return useMemo(function () {
    return {
      id: "form-" + id,
      name: "form",
      initialValue: initialValue.current,
      initialError: initialError.current,
      initialTouched: initialTouched.current,
      value: value,
      error: error,
      touched: touched,
      submission: submission,
      isValidating: isValidating,
      isSubmitting: isSubmitting,
      setValue: setValue,
      setError: setError,
      setTouched: setTouched,
      setValidating: setValidating,
      setSubmitting: setSubmitting,
      setSubmission: setSubmission,
      reset: reset,
      validate: validate
    };
  }, [id, initialValue, initialError, initialTouched, value, error, touched, submission, isValidating, isSubmitting, reset, validate]);
}

function insert(values, index, value) {
  var result = [].concat(values);
  result.splice(index, 0, value);
  return result;
}
/**
 * Adds a new value at a specific position to an array of values.
 *
 * This can be used to create a form with repeating fields.
 *
 * @example
 * const pets = useField(form, "pets");
 * const pet = useFieldItem(pets, 0);
 * const insert = useInsertItem(pets, 0, { name: "" });
 */


function useInsertItem(field, index, value) {
  var setValue = field.setValue,
      setError = field.setError,
      setTouched = field.setTouched;
  return useCallback(function () {
    setValue(function (values) {
      return insert(values, index, value);
    });
    setError(function (errors) {
      if (Array.isArray(errors)) {
        return insert(errors, index, undefined);
      } else {
        return errors;
      }
    });
    setTouched(function (touched) {
      if (Array.isArray(touched)) {
        return insert(touched, index, undefined);
      } else {
        return touched;
      }
    });
  }, [index, value, setValue, setError, setTouched]);
}

/**
 * Adds a new value to the end to an array of values.
 *
 * This can be used to create a form with repeating fields.
 *
 * @example
 * const pets = useField(form, "pets");
 * const pet = useFieldItem(pets, 0);
 * const addPet = usePushItem(pets, { name: ""  });
 */

function usePushItem(field, value) {
  var setValue = field.setValue;
  return useCallback(function () {
    setValue(function (values) {
      return [].concat(values, [value]);
    });
  }, [value, setValue]);
}

function remove(values, index) {
  var result = [].concat(values);
  result.splice(index, 1);
  return result;
}
/**
 * Removes a value at the given index from array of values.
 *
 * This can be used to create a form with repeating fields.
 *
 * @example
 * const pets = useField(form, "pets");
 * const pet = useFieldItem(pets, 0);
 * const removePet = useRemoveItem(pets, 0);
 */


function useRemoveItem(field, index) {
  var setValue = field.setValue,
      setError = field.setError,
      setTouched = field.setTouched;
  return useCallback(function () {
    setValue(function (values) {
      return remove(values, index);
    });
    setError(function (errors) {
      if (Array.isArray(errors)) {
        return remove(errors, index);
      } else {
        return errors;
      }
    });
    setTouched(function (touched) {
      if (Array.isArray(touched)) {
        return remove(touched, index);
      } else {
        return touched;
      }
    });
  }, [index, setValue, setError, setTouched]);
}

/**
 * If the value returned by the function is not a promise, convert it.
 * Make sure to catch errors that are thrown syncronously.
 */
function toPromise(fn) {
  try {
    return Promise.resolve(fn());
  } catch (error) {
    return Promise.reject(error);
  }
}

/**
 * @private
 *
 * Usually, an error message is only shown if a field has been touched. So,
 * when the form is submitted, we'll mark all erroneous fields as having
 * been touched.
 *
 * This function iterates over errors, replacing each error with `true`.
 */

function getAllTouched(errors) {
  if (errors === undefined) {
    return errors;
  }

  if (Array.isArray(errors)) {
    return errors.map(getAllTouched);
  }

  if (isPlainObject(errors)) {
    var result = {};

    for (var key in errors) {
      result[key] = getAllTouched(errors[key]);
    }

    return result;
  }

  return true;
}

function useMounted() {
  var isMounted = useRef(true);
  useEffect(function () {
    return function () {
      isMounted.current = false;
    };
  }, []);
  return isMounted;
}

/**
 * Use a plain ol' function for validation.
 *
 * This hook can also be used to incorporate your favorite validation library.
 *
 * @example
 * const validation = useValidation(form, value => {
 *   if (!value.email) {
 *     return { valid: false, error: { email: "can't be blank" } };
 *   }
 *
 *   return { valid: true, value };
 * });
 */

function useValidation(form, fn, opts) {
  if (opts === void 0) {
    opts = {};
  }

  var isMounted = useMounted();
  var _opts = opts,
      _opts$onChange = _opts.onChange,
      onChange = _opts$onChange === void 0 ? true : _opts$onChange,
      _opts$onBlur = _opts.onBlur,
      onBlur = _opts$onBlur === void 0 ? true : _opts$onBlur;
  var validate = useEventCallback(function (opts) {
    if (opts === void 0) {
      opts = {};
    }

    form.setValidating(true);
    return toPromise(function () {
      return fn(form.value);
    }).then(function (result) {
      var errors = result.valid ? undefined : result.error;

      if (isMounted.current) {
        form.setError(errors);

        if (opts.touch) {
          form.setTouched(getAllTouched(errors));
        }
      }

      return result;
    })["finally"](function () {
      if (isMounted.current) {
        form.setValidating(false);
      }
    });
  });
  useEffect(function () {
    if (onChange) validate();
  }, [form.value, onChange, validate]);
  useEffect(function () {
    if (onBlur) validate();
  }, [form.touched, onBlur, validate]);
  return useMemo(function () {
    return _extends({}, form, {
      validate: validate
    });
  }, [form, validate]);
}

/**
 * @private
 *
 * Update the value at a given path without mutating the original data.
 */
function setIn(data, path, value) {
  if (!path.length) {
    return value;
  }

  var next;
  var key = path[0],
      rest = path.slice(1);

  if (typeof key === "number") {
    next = Array.isArray(data) ? data.slice() : [];
  } else {
    next = Object.assign({}, data);
  }

  next[key] = setIn(next[key], rest, value);
  return next;
}

function convert(result) {
  if (result.valid) {
    return result;
  }

  var error = result.errors.reduce(function (error, err) {
    return setIn(error, err.path, err.message);
  }, undefined);
  return {
    valid: false,
    error: error
  };
}
/**
 * Add validation to the form using {@link https://github.com/rzane/validate @stackup/validate}.
 *
 * @example
 * const validator = schema({
 *   email: assert(isString).then(refute(isBlank))
 * });
 *
 * const validate = useValidate(form, validator);
 */


function useValidate(form, validator, opts) {
  return useValidation(form, function (value) {
    return validator.validate(value).then(convert);
  }, opts);
}

/**
 * Create a submit handler for the form.
 *
 * @example <caption>Sumbitting a form</caption>
 * const form = useForm({ initialValue: "foo" });
 * const submit = useSubmit(form, console.log);
 *
 * @example <caption>Sumbitting with validation</caption>
 * const form = useForm({ initialValue: "foo" });
 * const validate = useValidate(form, myValidator);
 * const submit = useSubmit(validate, console.log);
 */

function useSubmit(form, fn) {
  var isMounted = useMounted();
  return useEventCallback(function (event) {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    if (form.isSubmitting) {
      return Promise.resolve();
    }

    form.setSubmitting(true);
    return toPromise(function () {
      return form.validate({
        touch: true
      });
    }).then(function (result) {
      if (!result.valid) return;
      return toPromise(function () {
        return fn(result.value);
      }).then(function () {
        if (isMounted.current) {
          form.setSubmission(function (prev) {
            return {
              count: prev.count + 1
            };
          });
        }
      });
    })["catch"](function (error) {
      if (isMounted.current) {
        form.setSubmission(function (prev) {
          return {
            count: prev.count + 1,
            error: error
          };
        });
      }

      throw error;
    })["finally"](function () {
      if (isMounted.current) {
        form.setSubmitting(false);
      }
    });
  });
}

/**
 * Create a reset handler for the form.
 *
 * @example
 * const form = useForm({ initialValue: "foo" });
 * const reset = useReset(form);
 */

function useReset(form) {
  return useEventCallback(function (event) {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    form.reset();
    return Promise.resolve();
  });
}

export { useField, useFieldItem, useForm, useIdentifier, useInsertItem, usePushItem, useRemoveItem, useReset, useSubmit, useValidate, useValidation };
//# sourceMappingURL=form.esm.js.map
